// Insider trades (Research): one stock at a time. The API fetches the company's
// SEC Form 4 filings for the chosen range on demand, newest first and a page at
// a time, and cleans them with form4clean (services/insider); nothing is stored.

import { API_BASE } from "./api";

export const TRADE_TYPES = ["purchase", "sale", "award", "exercise", "tax", "gift", "conversion", "other", "unknown"] as const;
export type TradeType = (typeof TRADE_TYPES)[number];

export const TYPE_LABELS: Record<TradeType, string> = {
  purchase: "Purchase",
  sale: "Sale",
  award: "Award",
  exercise: "Option exercise",
  tax: "Tax withholding",
  gift: "Gift / transfer",
  conversion: "Conversion",
  other: "Other",
  unknown: "Unknown",
};

export const EXAMPLES = ["AAPL", "NVDA", "TSLA", "HOOD", "MSFT"];

/** Minimum trade value in dollars, with its label. */
export const SIZES: ReadonlyArray<[number, string]> = [[0, "Any"], [10_000, "$10K+"], [100_000, "$100K+"], [1_000_000, "$1M+"], [10_000_000, "$10M+"]];

/** Chart windows in months; 0 is everything loaded. */
export const CHART_RANGES: ReadonlyArray<[number, string]> = [[6, "6M"], [12, "1Y"], [24, "2Y"], [60, "5Y"], [0, "All"]];

/** Form 4s have been filed as XML since mid-2003; the lookup goes no further back. */
export const EARLIEST = "2004-01-01";

/** How far back to look: months, or 0 for everything since EARLIEST. */
export const LOOKBACKS: ReadonlyArray<[string, string, number]> = [["1y", "1Y", 12], ["2y", "2Y", 24], ["5y", "5Y", 60], ["10y", "10Y", 120], ["all", "All", 0]];
export const DEFAULT_LOOKBACK = "5y";

/** The filing dates a lookup covers, YYYY-MM-DD. */
export interface Window {
  since: string;
  until: string;
}

export function presetWindow(preset: string, today: string): Window {
  const months = LOOKBACKS.find(([id]) => id === preset)?.[2] ?? 60;
  return { since: months ? monthsBefore(today, months) : EARLIEST, until: today };
}

/** A custom range, or null when it is not one: both dates, in order, from EARLIEST to today. */
export function customWindow(since: string, until: string, today: string): Window | null {
  const day = /^\d{4}-\d{2}-\d{2}$/;
  if (!day.test(since) || !day.test(until) || since < EARLIEST || until > today || since >= until) return null;
  return { since, until };
}

/** Whole months between two days, rounded down. */
export function monthsBetween(since: string, until: string): number {
  const [a, b] = [new Date(`${since}T00:00:00Z`), new Date(`${until}T00:00:00Z`)];
  return (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + b.getUTCMonth() - a.getUTCMonth() - (b.getUTCDate() < a.getUTCDate() ? 1 : 0);
}

export interface Trade {
  id: string;
  acc: string;
  form: string;
  filed: string | null;
  filedDate: string | null;
  date: string | null;
  insider: string | null;
  role: string;
  title: string | null;
  type: TradeType;
  code: string | null;
  dir: "A" | "D" | null;
  security: string | null;
  deriv: boolean;
  shares: number | null;
  price: number | null;
  value: number | null;
  after: number | null;
  plan: boolean | null;
  url: string | null;
}

export type Bar = [day: string, close: number];

/** One answer: every trade in the newest `filings` of `totalFilings` filings. Page 1 also has prices and the cap. */
export interface TickerPage {
  ticker: string;
  issuer: string | null;
  cik: string;
  since: string;
  until: string;
  through: string;
  filings: number;
  totalFilings: number;
  complete: boolean;
  fetched: string;
  trades: Trade[];
  prices?: Bar[];
  cap?: number | null;
}

/** `limited`: the API's per-visitor rate limit answered; wait and ask again. */
export type Lookup = { ok: true; page: TickerPage } | { ok: false; error: string; limited?: boolean };

export async function lookUp(ticker: string, pages: number, window: Window, signal?: AbortSignal): Promise<Lookup> {
  const query = new URLSearchParams({ pages: String(pages), since: window.since, until: window.until });
  const response = await fetch(`${API_BASE}/v1/insider/tickers/${encodeURIComponent(ticker.toLowerCase())}?${query}`, { signal });
  const body = (await response.json().catch(() => null)) as (TickerPage & { error?: string }) | null;
  if (response.ok && body) return { ok: true, page: body };
  if (response.status === 429) return { ok: false, error: "Too many requests", limited: true };
  return { ok: false, error: body?.error ?? `The lookup failed (HTTP ${response.status}). Try again in a minute.` };
}

/** The API takes at most this many pages in one request. */
export const MAX_PAGES = 300;

/**
 * The next `pages` to ask for. A page that came straight from the lookup service's memory
 * (answered in under a second) doubles the step, up to 4, so a stock someone looked up
 * recently loads in a few requests instead of dozens, well inside the API's rate limit.
 */
export function nextPages(pages: number, step: number, answeredMs: number): { pages: number; step: number } {
  const next = answeredMs < 1000 ? Math.min(step * 2, 4) : 1;
  return { pages: Math.min(pages + next, MAX_PAGES), step: next };
}

export interface Suggestion {
  ticker: string;
  name: string;
}

export async function suggest(query: string, signal?: AbortSignal): Promise<Suggestion[]> {
  const response = await fetch(`${API_BASE}/v1/insider/search?q=${encodeURIComponent(query.trim().toLowerCase())}`, { signal });
  return response.ok ? ((await response.json()) as Suggestion[]) : [];
}

/** What a typed ticker becomes in the URL: upper case, share classes with a hyphen (BRK.B is BRK-B). */
export function normalizeTicker(text: string): string | null {
  const ticker = text.trim().toUpperCase().replace(/[./]/g, "-");
  return /^[A-Z0-9-]{1,12}$/.test(ticker) ? ticker : null;
}

const CAP_SIZES: ReadonlyArray<[number, string]> = [[200e9, "Mega cap"], [10e9, "Large cap"], [2e9, "Mid cap"], [300e6, "Small cap"], [0, "Micro cap"]];

export function capSize(cap: number): string {
  return CAP_SIZES.find(([floor]) => cap >= floor)![1];
}

/** True when three or more different insiders bought within 14 days of each other. */
export function clusterBuying(trades: readonly Trade[]): boolean {
  const buys = trades
    .filter((t) => t.type === "purchase" && t.date && t.insider)
    .map((t) => ({ day: t.date!, insider: t.insider! }))
    .sort((a, b) => a.day.localeCompare(b.day));
  return buys.some((start) => {
    const end = new Date(`${start.day}T00:00:00Z`);
    end.setUTCDate(end.getUTCDate() + 14);
    const until = end.toISOString().slice(0, 10);
    return new Set(buys.filter((b) => b.day >= start.day && b.day <= until).map((b) => b.insider)).size >= 3;
  });
}

export interface Summary {
  buys: number;
  buyValue: number;
  sells: number;
  sellValue: number;
  insiders: number;
}

export function summarize(trades: readonly Trade[]): Summary {
  const s: Summary = { buys: 0, buyValue: 0, sells: 0, sellValue: 0, insiders: new Set(trades.map((t) => t.insider)).size };
  for (const t of trades) {
    if (t.type === "purchase") {
      s.buys += 1;
      s.buyValue += t.value ?? 0;
    } else if (t.type === "sale") {
      s.sells += 1;
      s.sellValue += t.value ?? 0;
    }
  }
  return s;
}

/**
 * Trades grouped onto the chart's bars: each trade goes to the first bar on or
 * after its transaction date (a weekend trade lands on Monday). Trades before
 * the first bar or after the last are left off.
 */
export function tradesByBar(trades: readonly Trade[], bars: readonly Bar[]): Map<string, Trade[]> {
  const days = bars.map(([day]) => day);
  const out = new Map<string, Trade[]>();
  for (const t of trades) {
    if (!t.date || !days.length || t.date < days[0]!) continue;
    let lo = 0;
    let hi = days.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (days[mid]! < t.date) lo = mid + 1;
      else hi = mid;
    }
    const day = days[lo];
    if (!day) continue;
    const list = out.get(day) ?? [];
    list.push(t);
    out.set(day, list);
  }
  return out;
}

/** Trades of the chosen types worth at least minValue; a trade with no dollar value only passes "Any". */
export function filterTrades(trades: readonly Trade[], types: ReadonlySet<TradeType>, minValue: number): Trade[] {
  return trades.filter((t) => types.has(t.type) && (minValue === 0 || (t.value ?? 0) >= minValue));
}

/** The day `months` before `day` (YYYY-MM-DD), for the chart's window. */
export function monthsBefore(day: string, months: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - months);
  return d.toISOString().slice(0, 10);
}

/** How far back the loaded filings reach, as a share of the window from `until` back to `since`. */
export function reachedPercent(since: string, through: string, until: string, complete: boolean): number {
  if (complete) return 100;
  const span = Date.parse(until) - Date.parse(since);
  return span > 0 ? Math.max(0, Math.min(100, (100 * (Date.parse(until) - Date.parse(through))) / span)) : 0;
}

export type PinUnit = "day" | "week" | "month" | "quarter";

const UNIT_DAYS: ReadonlyArray<[PinUnit, number]> = [["day", 1], ["week", 7], ["month", 30.4], ["quarter", 91.3]];
const PIN_SPACING = 32;

/** The smallest period that leaves about PIN_SPACING pixels per pin across the chart's width. */
export function pinUnit(visibleDays: number, widthPx: number): PinUnit {
  const slots = Math.max(4, widthPx / PIN_SPACING);
  return (UNIT_DAYS.find(([, days]) => visibleDays / days <= slots) ?? UNIT_DAYS[3]!)[0];
}

/** The first day of the day, week (Monday), month or quarter that `day` falls in. */
export function bucketStart(day: string, unit: PinUnit): string {
  if (unit === "day") return day;
  if (unit === "month") return `${day.slice(0, 7)}-01`;
  if (unit === "quarter") return `${day.slice(0, 4)}-${String(Math.floor((Number(day.slice(5, 7)) - 1) / 3) * 3 + 1).padStart(2, "0")}-01`;
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

export interface PinGroup {
  /** The bar the pins sit on: the first bar with a trade in the period. */
  time: string;
  label: string;
  trades: Trade[];
}

const longDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
const longMonth = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

/** Trades grouped by period, keyed by the period's first day. Trades off the chart are left out. */
export function groupTrades(trades: readonly Trade[], bars: readonly Bar[], unit: PinUnit): Map<string, PinGroup> {
  const groups = new Map<string, PinGroup>();
  for (const [day, list] of [...tradesByBar(trades, bars)].sort(([a], [b]) => a.localeCompare(b))) {
    const key = bucketStart(day, unit);
    const group = groups.get(key);
    if (group) group.trades.push(...list);
    else {
      const date = new Date(`${key}T00:00:00Z`);
      const label = unit === "day" ? longDay.format(date)
        : unit === "week" ? `Week of ${longDay.format(date)}`
        : unit === "month" ? longMonth.format(date)
        : `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
      groups.set(key, { time: day, label, trades: [...list] });
    }
  }
  return groups;
}

/** Seconds as m:ss. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The SEC rate the lookup service runs at, for a first guess before any page has arrived. */
const FILINGS_PER_SECOND = 8;

/** Filings loaded `at` seconds into a lookup. */
export interface Sample {
  at: number;
  loaded: number;
}

const RATE_WINDOW = 20;

/**
 * Seconds left to load `total` filings, at the speed between the samples of the last
 * RATE_WINDOW seconds, so the estimate follows the SEC when it slows down. With fewer than two
 * samples it assumes the SEC's pace.
 */
export function secondsLeft(samples: readonly Sample[], total: number): number {
  const last = samples.at(-1);
  if (!last) return total / FILINGS_PER_SECOND;
  const from = samples.find((s) => s.at >= last.at - RATE_WINDOW) ?? last;
  const rate = last.at - from.at >= 1 && last.loaded > from.loaded ? (last.loaded - from.loaded) / (last.at - from.at) : FILINGS_PER_SECOND;
  return Math.max(0, (total - last.loaded) / rate);
}

/**
 * The calendar years on the progress bar, which runs from `until` (0) back to `since` (100):
 * each year's start and end as a share of the bar.
 */
export function yearSegments(since: string, until: string): Array<{ year: number; from: number; to: number }> {
  const span = Date.parse(until) - Date.parse(since);
  const at = (day: string) => Math.min(100, (100 * (Date.parse(until) - Date.parse(day))) / span);
  const thisYear = Number(until.slice(0, 4));
  const segments = [];
  for (let year = thisYear; year >= Number(since.slice(0, 4)); year--) {
    segments.push({ year, from: year === thisYear ? 0 : at(`${year + 1}-01-01`), to: at(`${year}-01-01`) });
  }
  return segments;
}

/** More filings than this (about two and a half minutes at the SEC's pace) and the page asks before loading them all. */
export const LONG_FILINGS = 1200;

/**
 * The longest preset shorter than the current range whose share of the filings stays under
 * LONG_FILINGS, assuming filings spread evenly over time; the shortest preset when none does.
 */
export function shorterPreset(totalFilings: number, spanMonths: number): { id: string; label: string; filings: number } {
  const options = LOOKBACKS.filter(([, , months]) => months > 0 && months < spanMonths).map(([id, label, months]) => ({
    id,
    label,
    filings: Math.round((totalFilings * months) / spanMonths),
  }));
  return options.filter((o) => o.filings <= LONG_FILINGS).at(-1) ?? options[0] ?? { id: "1y", label: "1Y", filings: totalFilings };
}
